import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { LogoMark, Wordmark } from "@/components/Logo";
import { scrollToId } from "@/lib/api";
import { SITE } from "@/content";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 120 : 0);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-[80] transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-[#180407]/80 backdrop-blur-xl border-b border-[#D4AF37]/20 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3 group">
          <span className="transition-transform duration-500 group-hover:rotate-[8deg]">
            <LogoMark size={38} />
          </span>
          <Wordmark />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {SITE.navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="relative text-[13px] uppercase tracking-[0.22em] text-[#FAF6F0]/80 hover:text-[#F3E5AB] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#D4AF37] hover:after:w-full after:transition-all after:duration-400"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-book"
            onClick={() => go("book")}
            className="relative overflow-hidden rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-6 py-2.5 text-[12px] uppercase tracking-[0.22em] text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#2C070B] transition-colors duration-400"
          >
            Enquire Now
          </button>
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            title="Admin portal"
            className="text-[#A39791] hover:text-[#D4AF37] transition-colors duration-300"
          >
            <ShieldCheck size={18} />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#F3E5AB] p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#180407]/95 backdrop-blur-xl border-b border-[#D4AF37]/20 px-6 pb-6 pt-2 flex flex-col gap-4">
          {SITE.navLinks.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left text-sm uppercase tracking-[0.22em] text-[#FAF6F0]/85 hover:text-[#F3E5AB] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-cta-book"
            onClick={() => go("book")}
            className="rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-6 py-3 text-[12px] uppercase tracking-[0.22em] text-[#F3E5AB]"
          >
            Enquire Now
          </button>
        </div>
      )}
    </header>
  );
};

export default Nav;
